/**
 * Sign in -- the app's front door (/ and /login).
 *
 * Replaces the marketing selector (Enterprise / Health Check / Investor pages, retired
 * 2026-10-03: "no marketing pages - they are all old and incorrect"). Invite-only: there
 * is no self-registration; an invite or reset email lands on /invite or /reset-password,
 * which sets the password and sends the user back here.
 *
 * Where a signed-in user goes is decided in one place, homeFor(): customers to their own
 * portal, everyone else to the customer list. That covers a password sign-in, the MFA
 * step, and arriving here already signed in.
 *
 * /login?break-glass is the emergency admin sign-in (POST /api/auth/break-glass). It is not
 * linked from anywhere; the backend refuses it unless break-glass is switched on.
 */
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../AuthProvider'
import '../Register/Register.css'
import './SignIn.css'

export function homeFor(profile) {
  return profile?.role === 'customer' ? '/portal' : '/customers'
}

const TITLES = {
  signin: { title: 'Sign in', sub: 'Use the email address your invitation was sent to.' },
  forgot: { title: 'Reset your password', sub: "Enter your email. If it has an account, we'll send a reset link." },
  mfa:    { title: 'Two-factor authentication', sub: 'Enter the 6-digit code from your authenticator app.' },
  breakGlass: { title: 'Emergency sign-in', sub: 'Break-glass access for platform admins. Every attempt is logged and alerted.' },
}

export default function SignIn() {
  const navigate = useNavigate()
  const breakGlass = new URLSearchParams(useLocation().search).has('break-glass')
  const { signIn, resetPassword, verifyMFA, isAuthenticated, profile, loading: authLoading } = useAuth()
  const [mode, setMode] = useState('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!authLoading && isAuthenticated && profile) navigate(homeFor(profile), { replace: true })
  }, [authLoading, isAuthenticated, profile, navigate])

  function switchMode(m) { setMode(m); setError(''); setNotice(''); setCode('') }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(''); setNotice('')
    setBusy(true)
    try {
      if (mode === 'forgot') {
        const r = await resetPassword(email)
        if (r.success) setNotice('If that address has an account, a reset link is on its way.')
        else setError(r.error || 'Could not send a reset link.')
      } else if (mode === 'mfa') {
        const r = await verifyMFA(code)
        if (!r.success) { setError(r.error || 'Invalid verification code.'); setCode('') }
      } else {
        const r = await signIn(email, password, { breakGlass })
        if (r.mfaRequired) switchMode('mfa')
        else if (!r.success) setError(r.error || 'Invalid email or password.')
      }
    } finally {
      setBusy(false)
    }
  }

  const t = breakGlass && mode === 'signin' ? TITLES.breakGlass : TITLES[mode]
  return (
    <div className="reg-page si-page">
      <div className="reg-card si-card">
        <div className="reg-logo si-logo">
          <img src="/sempirical-lockup.svg" alt="Sempirical" className="reg-wordmark" />
        </div>

        <h1 className="reg-title">{t.title}</h1>
        <p className="reg-subtitle">{t.sub}</p>
        {error && <div className="reg-error" role="alert">{error}</div>}
        {notice && <div className="si-notice" role="status">{notice}</div>}

        <form className="reg-form" onSubmit={handleSubmit}>
          {mode === 'mfa' ? (
            <div className="reg-field">
              <label className="reg-label" htmlFor="si-code">Verification code</label>
              <input id="si-code" className="reg-input si-code" value={code} placeholder="000000"
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code" inputMode="numeric" maxLength={6} autoFocus />
            </div>
          ) : (
            <>
              <div className="reg-field">
                <label className="reg-label" htmlFor="si-email">Email</label>
                <input id="si-email" className="reg-input" type="email" autoComplete="username"
                  value={email} onChange={e => setEmail(e.target.value)} autoFocus required />
              </div>
              {mode === 'signin' && (
                <div className="reg-field">
                  <label className="reg-label" htmlFor="si-password">Password</label>
                  <input id="si-password" className="reg-input" type="password" autoComplete="current-password"
                    value={password} onChange={e => setPassword(e.target.value)} required />
                </div>
              )}
            </>
          )}
          <button type="submit" className="reg-submit" disabled={busy || (mode === 'mfa' && code.length !== 6)}>
            {busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : mode === 'forgot' ? 'Send reset link' : 'Verify'}
          </button>
        </form>

        <div className="reg-footer">
          {mode === 'signin' && breakGlass ? null : mode === 'signin'
            ? <button type="button" className="si-linkbtn" onClick={() => switchMode('forgot')}>Forgot your password?</button>
            : <button type="button" className="si-linkbtn" onClick={() => switchMode('signin')}>Back to sign in</button>}
        </div>
        <p className="si-invite-note">Access is by invitation. If you need an account, contact your Sempirical Account Representative.</p>
      </div>
    </div>
  )
}
